import { installBackNavigation } from './back-navigation';

interface TelegramHapticFeedback {
  impactOccurred(style: "light" | "medium" | "heavy"): void;
  notificationOccurred?(type: "error" | "success" | "warning"): void;
}

export type TelegramHomeScreenStatus =
  | "unsupported"
  | "unknown"
  | "added"
  | "missed";

export type TelegramHomeScreenPlatform = "android" | "ios" | "other";

type TelegramEventType = "activated" | "deactivated" | "themeChanged" | "homeScreenAdded" | "shareMessageSent" | "shareMessageFailed";

interface TelegramWebApp {
  initData: string;
  initDataUnsafe?: {
    start_param?: string;
    user?: {
      id?: number;
      language_code?: string;
      allows_write_to_pm?: true;
    };
  };
  colorScheme: "light" | "dark";
  platform: string;
  isActive?: boolean;
  ready(): void;
  expand(): void;
  setHeaderColor(color: string): void;
  setBackgroundColor(color: string): void;
  enableVerticalSwipes?(): void;
  disableVerticalSwipes?(): void;
  isVersionAtLeast?(version: string): boolean;
  BackButton?: { show(): void; hide(): void; onClick(callback: () => void): void; offClick(callback: () => void): void };
  LocationManager?: {
    isInited: boolean; isLocationAvailable: boolean;
    init(callback: () => void): void;
    getLocation(callback: (location: { latitude: number; longitude: number } | null) => void): void;
  };
  onEvent?(eventType: TelegramEventType, callback: () => void): void;
  offEvent?(eventType: TelegramEventType, callback: () => void): void;
  shareMessage?(messageId: string, callback?: (sent: boolean) => void): void;
  requestWriteAccess?(callback?: (allowed: boolean) => void): void;
  addToHomeScreen?(): void;
  checkHomeScreenStatus?(
    callback?: (status: TelegramHomeScreenStatus) => void,
  ): void;
  openTelegramLink?(url: string): void;
  openLink?(url: string): void;
  openInvoice?(url: string, callback: (status: "paid" | "cancelled" | "failed" | "pending") => void): void;
  HapticFeedback?: TelegramHapticFeedback;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export interface TelegramContext {
  webApp: TelegramWebApp | null;
  isTelegram: boolean;
}

interface StartParameterLocation {
  search: string;
  hash: string;
}

export function initializeTelegram(): TelegramContext {
  const webApp = window.Telegram?.WebApp ?? null;
  if (!webApp) {
    return { webApp: null, isTelegram: false };
  }
  webApp.ready();
  installBackNavigation(webApp.BackButton);
  webApp.expand();
  if (webApp.isVersionAtLeast?.("7.7")) {
    webApp.disableVerticalSwipes?.();
  }
  webApp.setHeaderColor("secondary_bg_color");
  webApp.setBackgroundColor("bg_color");
  document.documentElement.dataset.telegram = "true";
  document.documentElement.dataset.colorScheme = webApp.colorScheme;
  return { webApp, isTelegram: true };
}

export function hapticTap(context: TelegramContext): void {
  context.webApp?.HapticFeedback?.impactOccurred("light");
}

export function hapticSuccess(context: TelegramContext): void {
  context.webApp?.HapticFeedback?.notificationOccurred?.("success");
}

export function telegramStartParameter(
  context: TelegramContext,
  location: StartParameterLocation = window.location,
): string | null {
  const telegramValue = context.webApp?.initDataUnsafe?.start_param;
  if (telegramValue) {
    return telegramValue;
  }
  const queryValue = new URLSearchParams(location.search).get(
    "tgWebAppStartParam",
  );
  if (queryValue) {
    return queryValue;
  }
  const hashValue = location.hash.startsWith("#")
    ? location.hash.slice(1)
    : location.hash;
  return hashValue || null;
}

export function telegramUserIdentifier(
  context: TelegramContext,
): string | null {
  const value = context.webApp?.initDataUnsafe?.user?.id;
  if (typeof value === "number" && Number.isSafeInteger(value)) {
    return String(value);
  }
  const encodedUser = context.webApp?.initData
    ? new URLSearchParams(context.webApp.initData).get("user")
    : null;
  if (!encodedUser) {
    return null;
  }
  try {
    const parsed = JSON.parse(encodedUser) as { id?: unknown };
    return typeof parsed.id === "number" && Number.isSafeInteger(parsed.id)
      ? String(parsed.id)
      : null;
  } catch {
    return null;
  }
}

export function telegramUserLanguageCode(
  context: TelegramContext,
): string | null {
  const unsafeValue = context.webApp?.initDataUnsafe?.user?.language_code;
  if (typeof unsafeValue === "string" && unsafeValue.trim()) {
    return unsafeValue.trim();
  }
  const encodedUser = context.webApp?.initData
    ? new URLSearchParams(context.webApp.initData).get("user")
    : null;
  if (!encodedUser) {
    return null;
  }
  try {
    const parsed = JSON.parse(encodedUser) as { language_code?: unknown };
    return typeof parsed.language_code === "string" && parsed.language_code.trim()
      ? parsed.language_code.trim()
      : null;
  } catch {
    return null;
  }
}

export function telegramUserAllowsWrite(
  context: TelegramContext,
): boolean {
  if (context.webApp?.initDataUnsafe?.user?.allows_write_to_pm === true) {
    return true;
  }
  const encodedUser = context.webApp?.initData
    ? new URLSearchParams(context.webApp.initData).get("user")
    : null;
  if (!encodedUser) {
    return false;
  }
  try {
    const parsed = JSON.parse(encodedUser) as {
      allows_write_to_pm?: unknown;
    };
    return parsed.allows_write_to_pm === true;
  } catch {
    return false;
  }
}

export type TelegramShareResult = boolean | null | "unknown";

// The official SDK keeps its own share-dialog lock until the native callback.
// We cannot reset that private lock. After a missing callback, a NEW explicit
// click uses the public link chooser instead; a late callback restores rich sharing.
const unconfirmedTelegramShares = new WeakSet<TelegramWebApp>();

export function sharePreparedTelegramMessage(
  context: TelegramContext,
  messageId: string,
): Promise<TelegramShareResult> {
  const webApp = context.webApp;
  const shareMessage = webApp?.shareMessage;
  if (!webApp || !shareMessage || unconfirmedTelegramShares.has(webApp)
      || (webApp.isVersionAtLeast && !webApp.isVersionAtLeast("8.0"))) {
    return Promise.resolve(null);
  }
  return new Promise((resolve, reject) => {
    const hostWindow = typeof window === "undefined" ? null : window;
    const hostDocument = typeof document === "undefined" ? null : document;
    const away = new Set<string>(webApp.isActive === false ? ["native"] : []);
    let settled = false;
    let resumeTimer: ReturnType<typeof setTimeout> | undefined;
    let watchdog: ReturnType<typeof setTimeout> | undefined;
    const subscriptions: [TelegramEventType, () => void][] = [];
    const cleanup = () => {
      clearTimeout(watchdog);
      clearTimeout(resumeTimer);
      for (const [event, handler] of subscriptions) webApp.offEvent?.(event, handler);
      hostDocument?.removeEventListener("visibilitychange", visibility);
      hostWindow?.removeEventListener("blur", blur);
      hostWindow?.removeEventListener("focus", focus);
      hostWindow?.removeEventListener("pagehide", pagehide);
      hostWindow?.removeEventListener("pageshow", pageshow);
    };
    const finish = (result: TelegramShareResult) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(result);
    };
    const unknown = () => {
      if (settled) return;
      unconfirmedTelegramShares.add(webApp);
      finish("unknown");
    };
    const leave = (source: string) => {
      away.add(source);
      clearTimeout(resumeTimer);
      resumeTimer = undefined;
    };
    const resume = (source: string) => {
      if (!away.delete(source) || resumeTimer !== undefined) return;
      // Native results can arrive just AFTER activated/visible/focus. Do not
      // mistake that ordinary ordering for a lost result or open another chooser.
      resumeTimer = setTimeout(unknown, 750);
    };
    const visibility = () => hostDocument?.visibilityState === "hidden"
      ? leave("document") : resume("document");
    const blur = () => leave("focus");
    const focus = () => resume("focus");
    const pagehide = () => leave("page");
    const pageshow = () => resume("page");
    const subscribe = (event: TelegramEventType, handler: () => void) => {
      if (!webApp.onEvent || !webApp.offEvent) return;
      webApp.onEvent(event, handler);
      subscriptions.push([event, handler]);
    };
    const confirmed = (sent: boolean) => {
      unconfirmedTelegramShares.delete(webApp);
      finish(sent);
    };
    try {
      subscribe("deactivated", () => leave("native"));
      subscribe("activated", () => resume("native"));
      subscribe("shareMessageSent", () => confirmed(true));
      subscribe("shareMessageFailed", () => confirmed(false));
      hostDocument?.addEventListener("visibilitychange", visibility);
      hostWindow?.addEventListener("blur", blur);
      hostWindow?.addEventListener("focus", focus);
      hostWindow?.addEventListener("pagehide", pagehide);
      hostWindow?.addEventListener("pageshow", pageshow);
      // Last-resort bound for clients with neither a callback nor lifecycle
      // events. This unlocks OUR UI only; it never retries or sends a message.
      watchdog = setTimeout(unknown, 120_000);
      shareMessage.call(webApp, messageId, confirmed);
    } catch (error) {
      cleanup();
      settled = true;
      if (error instanceof Error && error.message === "WebAppShareMessageOpened") {
        unconfirmedTelegramShares.add(webApp);
        resolve(null);
        return;
      }
      if (error instanceof Error && error.message === "WebAppMethodUnsupported") {
        resolve(null);
        return;
      }
      reject(error);
    }
  });
}

export function openTelegramShareLink(context: TelegramContext, url: string, text: string): boolean {
  if (!context.isTelegram || !context.webApp?.openTelegramLink) return false;
  const query = new URLSearchParams({ url, text });
  context.webApp.openTelegramLink(`https://t.me/share/url?${query}`);
  return true;
}

export function requestTelegramWriteAccess(
  context: TelegramContext,
): Promise<boolean | null> {
  const requestWriteAccess = context.webApp?.requestWriteAccess;
  if (!requestWriteAccess) {
    return Promise.resolve(null);
  }
  return new Promise((resolve, reject) => {
    try {
      requestWriteAccess.call(context.webApp, resolve);
    } catch (error) {
      reject(error);
    }
  });
}

export function checkTelegramHomeScreenStatus(
  context: TelegramContext,
): Promise<TelegramHomeScreenStatus | null> {
  const checkHomeScreenStatus = context.webApp?.checkHomeScreenStatus;
  if (!checkHomeScreenStatus) {
    return Promise.resolve(null);
  }
  return new Promise((resolve, reject) => {
    // Some older clients expose the method but never deliver its callback.
    // A timeout is an unknown result, not proof that installation is unsupported.
    const timer = globalThis.setTimeout(() => reject(new Error("Home-screen status timed out")), 2_000);
    try {
      checkHomeScreenStatus.call(context.webApp, (status) => {
        globalThis.clearTimeout(timer);
        resolve(status);
      });
    } catch (error) {
      globalThis.clearTimeout(timer);
      reject(error);
    }
  });
}

export function telegramHomeScreenPlatform(
  context: TelegramContext,
): TelegramHomeScreenPlatform {
  const platform = context.webApp?.platform?.trim().toLowerCase() ?? "";
  if (platform.startsWith("android")) {
    return "android";
  }
  if (platform === "ios") {
    return "ios";
  }
  return "other";
}

export function requestTelegramHomeScreenShortcut(
  context: TelegramContext,
): boolean {
  const addToHomeScreen = context.webApp?.addToHomeScreen;
  if (!addToHomeScreen) {
    return false;
  }
  try {
    addToHomeScreen.call(context.webApp);
    return true;
  } catch {
    return false;
  }
}

export function onTelegramHomeScreenAdded(
  context: TelegramContext,
  callback: () => void,
): () => void {
  const onEvent = context.webApp?.onEvent;
  if (!onEvent) {
    return () => undefined;
  }
  onEvent.call(context.webApp, "homeScreenAdded", callback);
  return () => {
    context.webApp?.offEvent?.("homeScreenAdded", callback);
  };
}

export function onTelegramActivated(
  context: TelegramContext,
  callback: () => void,
): () => void {
  const onEvent = context.webApp?.onEvent;
  if (!onEvent) {
    return () => undefined;
  }
  onEvent.call(context.webApp, "activated", callback);
  return () => {
    context.webApp?.offEvent?.("activated", callback);
  };
}

export function openTelegramBotStart(
  context: TelegramContext,
  url: string,
): void {
  if (context.webApp?.openTelegramLink) {
    context.webApp.openTelegramLink(url);
    return;
  }
  window.location.assign(url);
}

export async function authenticateTelegram(
  context: TelegramContext,
): Promise<boolean> {
  const initData = context.webApp?.initData;
  if (!initData) {
    return false;
  }
  const response = await fetch("/api/auth/telegram", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ initData }),
  });
  return response.ok;
}
